import React, { useCallback, useState } from 'react';

import { Alert, Box, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import QuickSearchToolbar from '~/components/Common/Elements/QuickSearchToolbar';

import { useGetAllBundles } from '~/hooks/apis/accommodation/useBundles';

import { Bundle, BundleHotelRule, BundleOffer } from '~/services/AccommodationService';

import BundleDetailDialog from './BundleDetailDialog';
import { BOARD_CODE_LABELS, bundleStatusPillProps } from './constants';

const columns: Array<GridColDef> = [
  {
    field: 'name',
    headerName: 'Bundle Name',
    flex: 2,
    minWidth: 200,
  },
  {
    field: 'id',
    headerName: 'Bundle ID',
    flex: 1,
    minWidth: 280,
  },
  {
    field: 'tagId',
    headerName: 'Tag ID',
    flex: 1,
    minWidth: 200,
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 120,
    renderCell: ({ value }) => {
      const { label, color } = bundleStatusPillProps(value as Bundle['status']);
      return <Chip label={label} color={color} size="small" variant="outlined" />;
    },
  },
  {
    field: 'offers',
    headerName: 'Offers',
    width: 100,
    valueGetter: (_value, row) => row.offers?.length ?? 0,
  },
  {
    field: 'rules',
    headerName: 'Rules',
    width: 100,
    valueGetter: (_value, row) => row.rules?.length ?? 0,
  },
  {
    field: 'schedules',
    headerName: 'Schedules',
    width: 100,
    valueGetter: (_value, row) => row.schedules?.length ?? 0,
  },
  {
    field: 'boardCodes',
    headerName: 'Board Codes',
    flex: 1,
    minWidth: 180,
    sortable: false,
    valueGetter: (_value, row) => {
      const allCodes = (row.rules ?? []).flatMap((r: BundleHotelRule) => r.boardCodes);
      return [...new Set(allCodes)];
    },
    renderCell: ({ value }) => (
      <Stack direction="row" spacing={0.5} flexWrap="wrap" alignItems="center" sx={{ py: 0.5 }}>
        {(value as Array<string>).map((code) => (
          <Chip key={code} label={BOARD_CODE_LABELS[code] ?? code} size="small" variant="outlined" />
        ))}
      </Stack>
    ),
  },
  {
    field: 'searchableIds',
    headerName: 'Associated IDs',
    valueGetter: (_value, row) => {
      const offerValues = (row.offers ?? []).flatMap((o: BundleOffer) => [
        o.entityId,
        o.propertyId,
        o.productId,
        o.experienceName,
        o.experienceDescription,
      ]);
      return offerValues.filter(Boolean).join(' ');
    },
  },
];

export default function AccommodationBundlesContainer() {
  const [selectedBundleId, setSelectedBundleId] = useState<string | null>(null);

  const { data, isLoading, error } = useGetAllBundles();

  const handleRowClick = useCallback((params: { row: Bundle }) => {
    setSelectedBundleId(params.row.id);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setSelectedBundleId(null);
  }, []);

  if (error) {
    return (
      <Alert severity="error">
        <Typography>Failed to load bundles: {error.message}</Typography>
      </Alert>
    );
  }

  return (
    <Box>
      {isLoading && (
        <Box p={4} display="flex" justifyContent="center">
          <CircularProgress variant="indeterminate" />
        </Box>
      )}

      {!isLoading && (
        <DataGrid
          rows={data?.bundles ?? []}
          columns={columns}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
            columns: {
              columnVisibilityModel: {
                searchableIds: false,
              },
            },
            filter: {
              filterModel: {
                items: [],
                quickFilterExcludeHiddenColumns: false,
              },
            },
          }}
          onRowClick={handleRowClick}
          autoHeight
          disableRowSelectionOnClick
          slots={{ toolbar: QuickSearchToolbar }}
          sx={{ cursor: 'pointer' }}
        />
      )}

      {selectedBundleId && (
        <BundleDetailDialog bundleId={selectedBundleId} open={!!selectedBundleId} onClose={handleCloseDialog} />
      )}
    </Box>
  );
}
